import React, { useState } from "react";
import { Box, Button } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, Link } from "react-router-dom";
import PasswordField from "../../Components/PasswordField";
import { useSnackbar } from "../../contexts/SnackbarProvider";
import { useApi } from "../../contexts/ApiProvider";
import { IUser } from "../users/types";
import { useTranslation } from "react-i18next";

type TRequestPassword = {
  password: string;
};

interface IPasswordFormProps {
  user: IUser;
}

const PasswordForm = (props: IPasswordFormProps) => {
  const { user } = props;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();
  const showMessage = useSnackbar();
  const api = useApi();
  const { t } = useTranslation();

  const handleCancel = () => {
    showMessage({
      text: t("Changing password cancelled"),
      severity: "info",
    });
    navigate("/");
  };

  const formik = useFormik({
    initialValues: {
      password: "",
      passwordConfirmation: "",
    },
    validationSchema: Yup.object({
      password: Yup.string().required(t("Password is required")),
      passwordConfirmation: Yup.string().oneOf(
        [Yup.ref("password"), undefined],
        t("Passwords must match")
      ),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      const result = await api.put<TRequestPassword, null>(
        `/users/${user.id}/password`,
        {
          password: window.btoa(values.password),
        }
      );
      if (result.ok) {
        navigate("/logout", {
          state: {
            title: t("Set Password"),
            message: t("Password changed successfully."),
          },
        });
      } else {
        showMessage({
          text: t("Changing password failed"),
          severity: "error",
        });
        navigate("/");
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <PasswordField
        fullWidth
        margin="normal"
        id="password"
        label={t("Password")}
        disabled={isSubmitting}
        error={formik.touched.password && Boolean(formik.errors.password)}
        helperText={formik.touched.password && formik.errors.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.password}
      ></PasswordField>
      <PasswordField
        fullWidth
        margin="normal"
        id="passwordConfirmation"
        label={t("Confirm Password")}
        disabled={isSubmitting}
        error={
          formik.touched.passwordConfirmation &&
          Boolean(formik.errors.passwordConfirmation)
        }
        helperText={
          formik.touched.passwordConfirmation &&
          formik.errors.passwordConfirmation
        }
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.passwordConfirmation}
      ></PasswordField>
      <Box mt={2} mb={2} display="flex" flexDirection="row">
        <Button type="submit" disabled={isSubmitting}>
          {t("Save")}
        </Button>
        <Link to="/">
          <Button type="reset" disabled={isSubmitting} onClick={handleCancel}>
            {t("Cancel")}
          </Button>
        </Link>
      </Box>
    </form>
  );
};

export default PasswordForm;
